import React, { useCallback, useState } from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';
import ParentForm from '../components/forms/ParentForm';
import ChildrenForm from '../components/forms/ChildrenForm';
import { useUser } from '../contexts/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import createUser from '../hooks/user/createUser';
import { isValidPassword } from '../utils/index';


const SignUpPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [parentFormData, setParentFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [childrenData, setChildrenData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleParentDataChange = (data: React.SetStateAction<{ fname: string; lname: string; email: string; phone: string; password: string; confirmPassword: string; }>) => {
    setParentFormData(data);
  };

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleRecaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      setSnackbar({ open: true, message: 'Please complete the reCAPTCHA verification.', severity: 'error' });
      return;
    }
    const recTokenResult = await executeRecaptcha('signUpForm');
    setRecaptchaToken(recTokenResult);
  }, [executeRecaptcha]);

  const handleSave = async () => {
    setLoading(true);

    await handleRecaptchaVerify();
    if (!recaptchaToken) {
      setSnackbar({ open: true, message: 'Please complete the reCAPTCHA verification.', severity: 'error' });
      setLoading(false);
      return;
    }

    if (!parentFormData.fname || !parentFormData.lname || !parentFormData.email || !parentFormData.phone) {
      setSnackbar({ open: true, message: 'All parent fields are required.', severity: 'error' });
      setLoading(false);
      return;
    }

    if (parentFormData.password !== parentFormData.confirmPassword) {
      setSnackbar({ open: true, message: 'Password and Confirm Password do not match.', severity: 'error' });
      setLoading(false);
      return;
    }

    if (!isValidPassword(parentFormData.password)) {
      setSnackbar({ open: true, message: 'Password is too weak. A minimum 8 characters password contains a combination of <strong>uppercase, lowercase letter</strong>,<strong>numbers</strong> and special characters like <strong>@$!%*#?&</strong>', severity: 'error' });
      setLoading(false);
      return;
    }

    if (childrenData.length == 0) {
      setSnackbar({ open: true, message: 'Please add at least 1 child.', severity: 'error' });
      setLoading(false);
      return;
    }

    const createUserResponse = await createUser(parentFormData.fname, parentFormData.lname, parentFormData.email, parentFormData.phone, parentFormData.password, childrenData);

    if (createUserResponse.success && createUserResponse.jwt && createUserResponse.payer_id) {
      setSnackbar({ open: true, message: 'Account successfully created. Check your email for an activation link to activate your user account.', severity: 'success' });
      setLoading(false);
    } else {
      setSnackbar({ open: true, message: createUserResponse.message, severity: 'error' });
    }
    setLoading(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (user && user.fname) {
    navigate('/signin');
  }

  return (
    <div className="event-page p-5 mb-20">
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
        }}
        autoComplete="off"
      >
        <div className="text-center mt-10">
          <ParentForm
            title="Create Parent Profile"
            subtitle=""
            disableInputs={false}
            onParentDataChange={handleParentDataChange}
          />

          <hr />

          <div className="mt-5">
            <ChildrenForm onChildrenDataChange={(data: any[]) => setChildrenData(data as never[])} />
          </div>

          <div className="mt-10 mb-5">
            <ButtonGroup variant="contained" aria-label="button group">
              <Button className="register-button gray"><Link to="/">Cancel</Link></Button>
              <Button className="register-button" disabled={loading} onClick={handleSave}>{loading ? 'Saving' : 'Save'}</Button>
            </ButtonGroup>
          </div>
        </div>
        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          handleClose={handleCloseSnackbar}
        />


        <GoogleReCaptcha
          onVerify={handleRecaptchaVerify}
          refreshReCaptcha={false}
        />
      </Box>
    </div>
  );
};

export default SignUpPage;
